import {
  Editor,
  FONT_FAMILIES,
  FONT_SIZES,
  renderHtmlFromRichTextForMeasurement,
  TEXT_PROPS,
  TLRichText,
  TLShape,
  TLShapeId,
  TLTextShape,
  toRichText,
} from "tldraw";
import { LabelAction } from "../../shared/schema/AgentActionSchemas";
import { Complete, Streaming } from "../../shared/types/Streaming";
import { AgentHelpers } from "../AgentHelpers";
import { AgentActionUtil, registerActionUtil } from "./AgentActionUtil";
import { debugWarn } from "@/utils/debugLog";

type ShapeWithRichText = Extract<TLShape, { props: { richText: TLRichText } }>;

const MIN_HUGGED_TEXT_WIDTH = 16;

/**
 * Measured width (in unscaled shape units) of rich text wrapped at
 * `maxWidthPx`, so fixed-width (autoSize=false) text boxes hug their rendered
 * content instead of claiming the full wrap cap. Uses the same measurement
 * pipeline as tldraw's TextShapeUtil (getTextSize), so the result matches the
 * actual render — the box edge sits on the glyphs, which keeps refs, anchors,
 * and annotations pointed at real content.
 */
function measureHuggedTextWidth(
  editor: Editor,
  richText: TLRichText,
  font: TLTextShape["props"]["font"],
  size: TLTextShape["props"]["size"],
  scale: number,
  maxWidthPx: number,
): number {
  const capW = Math.max(
    MIN_HUGGED_TEXT_WIDTH,
    Math.floor(maxWidthPx / (scale || 1)),
  );
  const html = renderHtmlFromRichTextForMeasurement(editor, richText);
  const measured = editor.textMeasure.measureHtml(html, {
    ...TEXT_PROPS,
    fontFamily: FONT_FAMILIES[font],
    fontSize: FONT_SIZES[size],
    maxWidth: capW,
  });
  // +1 mirrors tldraw's getTextSize: the measurement floors fractional widths,
  // and a too-tight box would re-wrap its last word.
  return Math.max(MIN_HUGGED_TEXT_WIDTH, Math.min(capW, measured.w + 1));
}

function isShapeWithRichText(
  shape: TLShape | null | undefined,
): shape is ShapeWithRichText {
  return !!(shape && "richText" in shape.props);
}

function assertShapeWithRichText(
  shape: TLShape | null | undefined,
): asserts shape is ShapeWithRichText {
  if (!isShapeWithRichText(shape)) {
    throw new Error("Shape is not a valid ShapeWithRichText");
  }
}

export const LabelActionUtil = registerActionUtil(
  class LabelActionUtil extends AgentActionUtil<LabelAction> {
    static override type = "label" as const;

    override getInfo(action: Streaming<LabelAction>) {
      return {
        icon: "pencil" as const,
        description: action.intent ?? "",
      };
    }

    override sanitizeAction(
      action: Complete<LabelAction>,
      helpers: AgentHelpers,
    ) {
      const shapeId = helpers.ensureShapeIdExists(action.shapeId);
      if (!shapeId) {
        return null;
      }
      const shape = this.editor.getShape(`shape:${shapeId}` as TLShapeId);
      if (!shape) {
        return null;
      }
      if (!isShapeWithRichText(shape)) {
        debugWarn(
          `Shape type "${shape.type}" does not support richText labels`,
        );
        return null;
      }
      action.shapeId = shapeId;
      return action;
    }

    override applyAction(action: Complete<LabelAction>) {
      const { editor } = this;

      const shapeId = `shape:${action.shapeId}` as TLShapeId;
      const shape = editor.getShape(shapeId);
      assertShapeWithRichText(shape);

      const richText = toRichText(action.text ?? "");
      const props: Record<string, unknown> = { richText };

      // Fixed-width text shapes created with a wrap cap hug their rendered
      // content — re-measure so the box tracks the new text (tldraw only
      // recomputes height for autoSize=false shapes, never width).
      if (shape.type === "text") {
        const textShape = shape as TLTextShape;
        const cap =
          typeof shape.meta?.maxWidth === "number"
            ? (shape.meta.maxWidth as number)
            : undefined;
        if (cap !== undefined && !textShape.props.autoSize) {
          props.w = measureHuggedTextWidth(
            editor,
            richText,
            textShape.props.font,
            textShape.props.size,
            textShape.props.scale,
            cap,
          );
        }
      }

      editor.updateShape({
        id: shapeId,
        type: shape.type,
        props,
      });
    }
  },
);
